import { makeAutoObservable } from 'mobx'
import { computedFn } from 'mobx-utils'

import type CheckableMap from './CheckableMap'

export class CheckableColl<T, K extends keyof T = keyof T> {
  constructor(private map: CheckableMap<T[K], T>) {
    makeAutoObservable(this)
  }

  get toArr() {
    return [...this.map.values()]
  }

  get entries() {
    return this.map.entries()
  }

  get size() {
    return this.map.size
  }

  values() {
    return this.map.values()
  }

  set(k: T[K], v: T) {
    return this.map.set(k, v)
  }

  delete(k: T[K]) {
    return this.map.delete(k)
  }

  deletes(keys: T[K][]) {
    return this.map.deletes(keys)
  }

  has(key: T[K]) {
    return this.map.has(key)
  }

  hasAll(keys: T[K][]) {
    return keys.every((key) => this.has(key))
  }

  setCheck(key: T[K]) {
    return this.map.setCheck(key)
  }

  setCheckAll() {
    return this.map.allCheck()
  }

  deleteCheck(key: T[K]) {
    return this.map.deleteCheck(key)
  }

  allCheck() {
    return this.map.allCheck()
  }

  get checkSize() {
    return this.map.checkSize
  }

  get isEmpty() {
    return this.map.isEmpty
  }

  get isNotEmpty() {
    return this.map.isNotEmpty
  }

  get toCheckedKeys() {
    return this.map.toCheckedKeys
  }

  get toCheckedValues() {
    return this.map.toCheckedValues
  }

  get isPartChecked() {
    return this.map.isPartChecked
  }

  get isAllChecked() {
    return this.map.isFullyChecked
  }

  get(k: T[K]) {
    return this.map.get(k)
  }

  clearAllCheck() {
    this.map.clearAllCheck()
  }

  toggleCheck(v: T[K]) {
    this.map.toggleCheck(v)
  }

  isChecked = computedFn((k: T[K]) => {
    return this.map.isChecked(k)
  })

  adds(coll: CheckableColl<T, K>) {
    this.map.adds(coll.map.entries())
  }
}
